// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { connect } from "react-redux";
import { ChartData, ChartOptions, ScatterDataPoint } from "chart.js";

import { Aggregation } from "graphql/api";
import { isSet } from "shared/utils/utils";
import { getColor } from "guiutils";
import { IState } from "states/states";
import { IInput } from "shared/types/parameter";
import { IIoTrend } from "states/trendingstates";

import { IDropdownSelectOption } from "./DropdownSelect";
import IoGraphBaseComponent, {
  IoChartType,
  mapBaseStateToProps,
  mapDispatchToProps,
} from "./IoGraphBase";

import cssContent from "constants.module.scss";

const LINE_WIDTH = 2;
// analog values are recorded on change by more than the hysteresis, at most every minInterval
// (at least 1 s); a longer gap with a bigger change means the value jumped, so keep the line
// flat there; changes of about one hysteresis step are a slow drift and stay interpolated
const HOLD_FACTOR = 2; // tolerance for timing jitter and value steps
const HOLD_MIN_RANGE_RATIO = 0.05; // smaller changes are hardly visible (and cover quantized values)
const HOLD_STEP_MS = 1000;

// hold: hidden helper point keeping the previous value, not a measurement
type IGraphPoint = { x: number; y: number; hold?: boolean };

// insert a hold point before each jump, so the line drops/rises just at the next value
function addHoldPoints(
  points: IGraphPoint[],
  trending: { minInterval?: number; hysteresis?: number } | undefined,
): IGraphPoint[] {
  const holdGapMs = HOLD_FACTOR * Math.max(trending?.minInterval ?? 0, 1) * 1000;
  // values may be quantized coarser than the hysteresis (e.g. integer SoC): ignore small changes
  let min = Infinity;
  let max = -Infinity;
  points.forEach((point) => {
    min = Math.min(min, point.y);
    max = Math.max(max, point.y);
  });
  const holdDelta = Math.max(
    HOLD_FACTOR * (trending?.hysteresis ?? 0),
    HOLD_MIN_RANGE_RATIO * (max - min),
  );
  return points.flatMap((point, ind) => {
    const prev = points[ind - 1];
    return prev && point.x - prev.x > holdGapMs && Math.abs(point.y - prev.y) > holdDelta ?
        [{ x: point.x - HOLD_STEP_MS, y: prev.y, hold: true }, point]
      : [point];
  });
}

interface IIoGraphAnalogProps {
  ioTrend: IIoTrend;
  showAllData: boolean; // raw values, not aggregated
  lastTrendData: number | undefined;
}

const mapStateToProps = (state: IState) => {
  const baseProps = mapBaseStateToProps(state);
  const showAllData = baseProps.aggregation === Aggregation.NONE;
  const props: IIoGraphAnalogProps = {
    ioTrend: showAllData ? state.trending.ioTrendFull : state.trending.ioTrendAggr,
    showAllData,
    lastTrendData: state.trending.maxTrendTimestamp,
  };
  return { ...baseProps, ...props };
};

// line graph of analog IOs
class IoGraphAnalogComponent extends IoGraphBaseComponent<IIoGraphAnalogProps> {
  protected readonly ioType = "analog";

  // the legend is drawn in reverse order, so we reverse the data sets as well
  protected orderSelectedIos(ios: IDropdownSelectOption[]) {
    return ios.reverse();
  }

  protected preProcessData() {
    const startTime = this.props.graphStart!;
    const endTime = this.props.graphEnd!;
    const timestamps = Object.keys(this.props.ioTrend).map((item) => parseInt(item));

    this.data = this.selectedIos.map((selectedIo) => {
      if (selectedIo.visible !== true) return [];

      const sourceIndex = selectedIo.source === "input" ? 0 : 1;
      const valueAt = (timestamp: number) =>
        this.props.ioTrend[timestamp]?.[sourceIndex]?.[selectedIo.index] as number | undefined;
      // neighbor values outside the period, so the line is drawn up to the graph borders
      const firstEntry = timestamps.find((ts) => ts < startTime && valueAt(ts) !== undefined);
      const lastEntry = timestamps.find((ts) => ts >= endTime && valueAt(ts) !== undefined);

      const values = timestamps
        .filter((ts) => ts >= startTime && ts < endTime && !isNaN(valueAt(ts)!))
        .map((ts) => [ts, valueAt(ts)!]);

      if (values.length > 0) {
        if (this.props.showAllData && isSet(firstEntry) && isSet(valueAt(firstEntry))) {
          values.unshift([firstEntry, valueAt(firstEntry)!]);
        }
        if (isSet(lastEntry)) {
          values.push([lastEntry, valueAt(lastEntry)!]);
        } else {
          this.addEndValue(values);
        }
      } else if (startTime) {
        // no value within the period: show the first known value as a flat line
        const knownEntry = firstEntry ?? lastEntry;
        if (isSet(knownEntry)) {
          values.push([startTime, valueAt(knownEntry) ?? 0]);
          if (!isSet(lastEntry)) this.addEndValue(values);
        }
      }
      return values;
    });
  }

  // continue the last value up to the last received data
  private addEndValue(values: number[][]) {
    if (!isSet(this.props.lastTrendData) || !this.props.graphEnd) return;
    const [lastTimestamp, lastValue] = values[values.length - 1];
    const endDate = Math.min(this.props.lastTrendData, this.props.graphEnd);
    if (lastTimestamp < endDate) {
      values.push([endDate, lastValue]);
    }
  }

  protected createGraphOptions() {
    const isLegendTop = this.isLegendTop();
    const options: ChartOptions<IoChartType> = {
      scales: {
        x: this.createTimeScale(),
        y: {
          type: "linear",
          beginAtZero: true,
          display: true,
        },
      },
      animation: {
        duration: 1000,
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      elements: {
        point: {
          radius: (ctx) => ((ctx.raw as IGraphPoint | undefined)?.hold ? 0 : 2),
          hitRadius: (ctx) => ((ctx.raw as IGraphPoint | undefined)?.hold ? 0 : 1),
        },
      },
      datasets: {
        line: {
          // smooth, but without overshooting or bending back in time (realistic SoC, hold points)
          cubicInterpolationMode: "monotone",
        },
      },
      plugins: {
        legend: {
          display:
            this.selectedIos.length > 1 &&
            (window.innerWidth > parseInt(cssContent.GRID_BREAKPOINT_MD) || isLegendTop),
          reverse: true,
          position: isLegendTop ? "top" : "right",
          onClick: (evt, legendItem) => this.legendItemClick(evt, legendItem),
        },
        tooltip: {
          enabled: true,
          mode: "point",
          position: "nearest", // show tooltip on average value, not at mouse cursor
          intersect: true,
          itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
          callbacks: {
            title: (tooltipItems) => this.createTooltipTitle(tooltipItems),
            label: (tooltipItem) => {
              // do we face a background fill data set?
              if (
                tooltipItem.dataset.label === undefined ||
                this.selectedIos[tooltipItem.datasetIndex]?.visible !== true
              ) {
                return undefined as unknown as string; // we need to disable the wrong type check here
              }
              const unit = (this.parameter[tooltipItem.datasetIndex] as IInput | undefined)?.unit;
              const value =
                unit ? `${tooltipItem.formattedValue} ${unit}` : tooltipItem.formattedValue;
              return ` ${tooltipItem.dataset.label}: ${value}`;
            },
          },
        },
        zoom: this.createZoomOptions(),
        datalabels: {
          display: false,
        },
      },
    };
    return options;
  }

  protected createGraphData() {
    const data: ChartData<IoChartType, ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    // hidden IOs need a data set as well, to show them in the legend
    this.data.forEach((dataset, anIndex) => {
      const selectedIo = this.selectedIos[anIndex];
      const color = getColor(selectedIo?.index ?? 0);
      let points: IGraphPoint[] = dataset.map((curItem) => ({
        x: curItem[0] * 1000, // timestamp
        y: curItem[1],
      }));
      // aggregated data is regular, only raw values are recorded on change
      if (this.props.showAllData) {
        points = addHoldPoints(points, this.parameter[anIndex]);
      }

      data.datasets.push({
        label: this.parameter[anIndex]?.name,
        data: points,
        type: "line",
        fill: false,
        hidden: selectedIo?.visible === false,
        borderWidth: LINE_WIDTH,
        borderColor: color,
        backgroundColor: color,
      });
    });
    return data;
  }
}

const IoGraphAnalog = connect(mapStateToProps, mapDispatchToProps)(IoGraphAnalogComponent);

export default IoGraphAnalog;
